import { useLingui } from '@lingui/react'
import classNames from 'clsx'
import { type FC } from 'react'
import { useDispatch } from 'react-redux'

import {
  EXPERIMENT_VARIATION,
  TOTAAL_KORTING_AB_TEST,
} from 'shared/experiments/consts'
import { showModalDeclarativeAction } from 'shared/store/ducks/events'
import IconCircleInfoOutline from 'views/components/atoms/Icon/IconCircleInfoOutline'
import IconTick from 'views/components/atoms/Icon/IconTick'
import Price from 'views/components/atoms/Price'
import RedPointsInformationModal from 'views/components/organisms/Checkout/RedPointsInformationModal'
import { RED_POINTS_INFORMATION_MODAL } from 'views/components/organisms/Checkout/RedPointsInformationModal/names'
import ShippingCostInformationModal from 'views/components/organisms/Checkout/ShippingCostInformationModal'
import { SHIPPING_COST_INFORMATION_MODAL } from 'views/components/organisms/Checkout/ShippingCostInformationModal/names'
import { CreditBalanceInformationModal } from 'views/components/organisms/CreditBalanceInformationModal'
import { CREDIT_BALANCE_INFORMATION_MODAL } from 'views/components/organisms/CreditBalanceInformationModal/names'
import Experiment from 'views/components/utils/optimizely/Experiment'
import Variant from 'views/components/utils/optimizely/Variant'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import { useLinePercentPromotions } from 'views/enhancers/useLinePercentPromotions'

import AppliedPromotionInformationModal from './AppliedPromotionInformationModal'
import { APPLIED_PROMOTION_INFORMATION_MODAL } from './AppliedPromotionInformationModal/names'

/**
 * @currency {@link Price}
 */
type Props = {
  label: string
  value: number
  modal?: string
  currency?: string
  purpose?: string
  infoIconDataQaId?: string
  rowDataQaId?: string
  priceDataQaIdPostfix?: string
  title?: string
}

const CostDetails: FC<Props> = ({
  label,
  value,
  modal,
  currency = 'EUR',
  purpose,
  infoIconDataQaId,
  rowDataQaId,
  priceDataQaIdPostfix,
  title,
}) => {
  const { i18n } = useLingui()
  const dispatch = useDispatch()

  const { linePercentPromotions } = useLinePercentPromotions()

  let dataQaId = 'cart-sumBox-'
  switch (purpose) {
    case 'voucher':
      dataQaId += `voucherSum-${priceDataQaIdPostfix ?? label}`
      break
    case 'rxDiscount':
      dataQaId += 'rxDiscount'
      break
    case 'shippingCosts':
      dataQaId += 'shippingCosts'
      break
    case 'RedPoints':
      dataQaId += 'redpointsDiscount'
      break
    case 'creditBalance':
      dataQaId += 'creditBalanceDiscount'
      break
    case 'paymentCost':
      dataQaId += 'paymentCost'
      break
    case 'appliedPromotions':
      dataQaId += 'appliedPromotions'
      break
    default:
      dataQaId += 'misc'
  }

  const getLinePromotionDetail = () =>
    linePercentPromotions.length > 0 ? (
      <Experiment name={TOTAAL_KORTING_AB_TEST} preventActivation>
        <Variant name={EXPERIMENT_VARIATION.DEFAULT}>
          {() => (
            <IconTick className="o-CartSumBox__list-item-icon-promotion-active" />
          )}
        </Variant>

        <Variant name={EXPERIMENT_VARIATION.V3}>
          {() => <Price amount={value} currency={currency} dataQa={dataQaId} />}
        </Variant>
      </Experiment>
    ) : (
      <IconTick className="o-CartSumBox__list-item-icon-promotion-active" />
    )

  return (
    <>
      {label && (
        <div
          className="u-margin-small--bottom l-flex l-flex--distribute-space o-CartSumBox__list-item o-CartSumBox__list-item--secondary"
          data-qa-id={rowDataQaId}
        >
          <span
            className="l-flex u-overflow-hidden l-flex__grow-one"
            title={title}
          >
            <TransWithHtml
              text={i18n._(label)}
              className="a-truncate-with-ellipsis"
            />
            {modal && (
              <span
                // @ts-ignore
                name={modal}
                data-qa-id={infoIconDataQaId}
                onKeyDown={() => dispatch(showModalDeclarativeAction(modal))}
                tabIndex={0}
                role="button"
                className="u-padding-x-small--left u-color--blue o-CartSumBox__list-item-icon-wrapper"
                onClick={() => dispatch(showModalDeclarativeAction(modal))}
                data-a11y-dialog-show={modal}
              >
                <div className="a-link">
                  <IconCircleInfoOutline size="small" />
                </div>
              </span>
            )}
            {modal === SHIPPING_COST_INFORMATION_MODAL ? (
              <ShippingCostInformationModal />
            ) : null}
            {modal === RED_POINTS_INFORMATION_MODAL ? (
              <RedPointsInformationModal />
            ) : null}
            {modal === CREDIT_BALANCE_INFORMATION_MODAL ? (
              <CreditBalanceInformationModal />
            ) : null}
            {modal === APPLIED_PROMOTION_INFORMATION_MODAL ? (
              <AppliedPromotionInformationModal title={label} />
            ) : null}
          </span>

          <span
            className={classNames('a-p o-CartSumBox__list-item-price-wrapper', {
              'u-color--positive u-color-rebranding--positive': value <= 0,
            })}
          >
            {purpose === 'appliedPromotions' ? (
              getLinePromotionDetail()
            ) : (
              <Price amount={value} currency={currency} dataQa={dataQaId} />
            )}
          </span>
        </div>
      )}
    </>
  )
}

export default CostDetails
